import React, { useCallback, useEffect, useState } from 'react';
import { Container, Box, Grid, Button, useMediaQuery, useTheme } from '@mui/material';
import ORMDataGridV2 from '../Shared/Table/ORMDataGridV2';
import APIService from 'src/utils/APIService';
import { ONIX_PRODUCT_PRICES_COLUMNS, ONIX_PRODUCTS_COLUMNS } from 'src/constants/dataGridColumns';
import RenderSpinner from '../Spinner';
import { useLocation } from 'react-router';
import Heading from '../Shared/UI/Heading';
import InputControl from '../Shared/Forms/InputControl';
import { FormatDateTime } from 'src/utils/DateTimeHelper';
import { useGridApiRef } from '@mui/x-data-grid-pro';

const OnixProducts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const search = useLocation().search;

  let isbn = new URLSearchParams(search).get('isbn');

  const [isLoading, setIsLoading] = useState();
  const [onixProducts, setOnixProducts] = useState([]);
  const [inputValue, setInputValue] = useState({
    startdate: FormatDateTime().format('YYYY-MM-DD'),
    enddate: FormatDateTime().format('YYYY-MM-DD'),
  });
  // State for expanded detail panels
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.resetRowHeights(); // force recalculation
    }
  }, [detailPanelExpandedRowIds]);

  const fetchData = useCallback(async dates => {
    let queryParams = new URLSearchParams();
    if (isbn) {
      queryParams.append('isbn', isbn);
    }

    if (!isbn && dates.startdate && dates.enddate) {
      queryParams.append('startdate', dates.startdate);
      queryParams.append('enddate', dates.enddate);
    }

    const url = `/onix/products/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    setOnixProducts([]);
    setIsLoading(true);
    try {
      const response = await APIService.get(url);
      setOnixProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setOnixProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(inputValue);
    document.title = 'Onix Products - Peggy';
  }, [fetchData]);

  const getDetailPanelContent = ({ row }) => {
    if (row.prices?.length > 0) {
      return (
        <Box
          sx={{
            p: 2,
          }}
        >
          <ORMDataGridV2
            getRowId={row =>
              row['currency'] + '_' + row['priceType'] + '_' + row['amount'] + '_' + row['effectiveDate']
            }
            columns={ONIX_PRODUCT_PRICES_COLUMNS}
            rows={row.prices || []}
            singleLineHeader
            pagination
          />
        </Box>
      );
    } else {
      return null;
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
  };

  const handleSubmit = () => fetchData(inputValue);

  const handleDetailPanelExpandedRowIdsChange = useCallback(newIds => {
    setDetailPanelExpandedRowIds(Array.from(newIds));
  }, []);

  return (
    <Container>
      {isLoading && <RenderSpinner message='Please wait ... ' />}
      <Heading heading='Onix Products' />
      <Grid
        container
        spacing={3}
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          {!isbn && (
            <Grid
              container
              spacing={2}
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputControl
                  label='Start Date'
                  name='startdate'
                  type='date'
                  onChange={handleInputChange}
                  value={inputValue?.startdate}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputControl
                  label='End Date'
                  name='enddate'
                  type='date'
                  onChange={handleInputChange}
                  value={inputValue?.enddate}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Button
                  onClick={handleSubmit}
                  disabled={!(inputValue.startdate && inputValue.enddate)}
                  fullWidth={isMobile}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Box>
        <ORMDataGridV2
          apiRef={apiRef}
          columns={ONIX_PRODUCTS_COLUMNS}
          rows={onixProducts}
          uniqueRowId='_id'
          pagination
          height={isMobile ? 'calc(100dvh - 320px)' : 'calc(100dvh - 270px)'}
          loading={isLoading}
          pageSizeOptions={[50, 100, 200, 500]}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => 'auto'}
          detailPanelExpandedRowIds={detailPanelExpandedRowIds || []}
          onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
        />
      </Box>
    </Container>
  );
};

export default OnixProducts;
